import { useEffect, useRef, type CSSProperties } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import gsap from "gsap";
import { moduleRegistry } from "@/modules";
import { useModuleTransitionStore } from "../store/module-transition-store";

const EXPAND_DURATION = 0.55;
const REVEAL_DURATION = 0.6;
/** Hedef route bu süre içinde gelmezse (engellendi, hata) perde zorla kalkar. */
const WATCHDOG_MS = 4000;

const isAbsolute = (href: string) => /^https?:\/\//.test(href);

/**
 * Hub kartından modüle geçişin tam ekran perdesi. AppRoot'ta durur; akış:
 *
 *   expanding  — kartın rect'inden tam ekrana büyür (modülün kendi rengi)
 *   expanded   — navigate(); lazy route yüklenip pathname değişene dek bekler
 *   revealing  — yeni sayfa altında hazır; perde solup çekilir, store temizlenir
 *
 * Kart→perde→sayfa zinciri sayesinde modül rengi ekrana "yayılır", ardından
 * ModuleThemeSync aynı hue'yu <html>'e yazdığı için renk sürekliliği bozulmaz.
 */
export const ModuleTransitionVeil = () => {
  const veil = useModuleTransitionStore((s) => s.veil);
  const setPhase = useModuleTransitionStore((s) => s.setPhase);
  const clearVeil = useModuleTransitionStore((s) => s.clearVeil);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const ref = useRef<HTMLDivElement>(null);
  const startPath = useRef<string | null>(null);
  const veilId = veil?.moduleId ?? null;

  // Faz 1: büyüme + navigasyon. Yalnızca perde ilk kurulduğunda çalışır.
  useEffect(() => {
    const el = ref.current;
    const current = useModuleTransitionStore.getState().veil;
    if (!el || !current || veilId === null) return;

    startPath.current = window.location.pathname;
    const tl = gsap.timeline({ defaults: { ease: "power3.inOut" } });
    tl.to(el, {
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: 0,
      duration: EXPAND_DURATION,
    })
      .fromTo(
        el.querySelector("[data-veil-icon]"),
        { scale: 0.5, opacity: 0 },
        { scale: 1, opacity: 1, duration: 0.4, ease: "back.out(1.6)" },
        `-=${EXPAND_DURATION * 0.5}`,
      )
      .add(() => {
        setPhase("expanded");
        if (isAbsolute(current.href)) window.location.assign(current.href);
        else navigate(current.href);
      });

    return () => {
      tl.kill();
    };
  }, [veilId, navigate, setPhase]);

  // Faz 2: hedef konum geldi → perdeyi çek.
  // setPhase("revealing") bu effect'in bağımlılığını değiştirdiği için tween
  // cleanup'ta değil, ref üzerinden yalnızca unmount'ta öldürülür.
  const phase = veil?.phase;
  const revealTween = useRef<gsap.core.Tween | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || phase !== "expanded") return;
    if (pathname === startPath.current) return;

    setPhase("revealing");
    revealTween.current = gsap.to(el, {
      opacity: 0,
      scale: 1.03,
      duration: REVEAL_DURATION,
      ease: "power2.out",
      onComplete: () => {
        revealTween.current = null;
        clearVeil();
      },
    });
  }, [phase, pathname, setPhase, clearVeil]);
  useEffect(
    () => () => {
      revealTween.current?.kill();
    },
    [],
  );

  // Bekçi: navigasyon gerçekleşmezse kullanıcıyı renkli ekranda bırakma
  useEffect(() => {
    if (phase !== "expanded") return;
    const id = window.setTimeout(clearVeil, WATCHDOG_MS);
    return () => window.clearTimeout(id);
  }, [phase, clearVeil]);

  if (!veil) return null;

  const instance = moduleRegistry.getModuleById(veil.moduleId);
  const Icon = instance?.icon;
  const { rect } = veil;

  return (
    <div
      ref={ref}
      aria-hidden
      style={
        {
          "--module-hue": veil.hue,
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          borderRadius: rect.radius,
        } as CSSProperties
      }
      className="module-scope fixed z-[1000] overflow-hidden bg-module-accent will-change-[top,left,width,height,opacity,transform]"
    >
      <span
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(70%_60%_at_25%_20%,rgba(255,255,255,0.28),transparent_65%)]"
      />
      {Icon && (
        <span
          data-veil-icon
          className="absolute inset-0 flex items-center justify-center text-module-accent-foreground opacity-0"
        >
          <Icon size={72} />
        </span>
      )}
    </div>
  );
};
